import {defineStore, storeToRefs} from 'pinia'
import {ISocketStoreState} from './types/ISocketStoreState'
import {io} from 'socket.io-client'
import {GlobalSocketEventType} from '@toolify/server/src/services/SocketService/enum/GlobalSocketEventType'
import {useAuthStore} from '@toolify/client/src/stores/AuthStore/useAuthStore'
import {watch} from 'vue'
import {$} from '@vue-macros/reactivity-transform/macros'
import {KnownError} from '@toolify/server/src/middleware/ApiResponseMiddleware/types/KnownError'
import {getRuntimeEnv} from '@toolify/client/src/stores/AdminRuntimeStore/functions/getRuntimeEnv'
import {isGodModeEnabled} from '@toolify/client/src/stores/AdminRuntimeStore/functions/isGodModeEnabled'

const MAX_FORBIDDEN_CONNECT_RETRY_COUNT = 20
const FORBIDDEN_CONNECT_RETRY_DELAY_STEP_MS = 1000
const MAX_FORBIDDEN_CONNECT_RETRY_DELAY_MS = 30000

let reconnectWatchdogInterval: ReturnType<typeof setInterval> | null = null
let visibilityChangeHandler: (() => void) | null = null

export const useSocketStore = defineStore('socket', {
  state: () => {
    return {
      socket: null,
      isConnected: false,
      methodsWaitingToBeInjected: [],
      areResourcePermissionLoadedMap: new Map(),
      connectRetryCount: 0,
    } as ISocketStoreState
  },
  actions: {
    on(eventType, fn) {
      if(!this.socket) {
        return this.methodsWaitingToBeInjected.push({
          eventType,
          fn,
          action: 'on',
        })
      }
      this.socket.on(eventType, fn)
    },
    onAny(fn) {
      if(!this.socket) {
        return this.methodsWaitingToBeInjected.push({
          fn,
          action: 'onAny',
        })
      }
      this.socket.onAny(fn)
    },
    offAny(fn) {
      if(!this.socket) {
        return this.methodsWaitingToBeInjected.push({
          fn,
          action: 'offAny',
        })
      }
      this.socket.offAny(fn)
    },
    off(eventType, fn) {
      if(!this.socket) {
        return this.methodsWaitingToBeInjected.push({
          eventType,
          fn,
          action: 'off',
        })
      }
      this.socket.off(eventType, fn)
    },
    initialize() {
      if(this.socket) {
        return
      }
      const authStore = useAuthStore()
      const {hasAuthToken} = $(storeToRefs(authStore))
      const runtimeEnv = getRuntimeEnv()
      const socket = this.socket = io(runtimeEnv.SocketIoHost, {
        path: runtimeEnv.SocketIoPath,
        withCredentials: true,
        reconnection: true,
        autoConnect: false,
        transports: ['websocket'],
        auth: async (callback) => {
          const token = await authStore.getToken()
          callback({
            token,
            godMode: isGodModeEnabled(),
          })
        },
      })
      socket.on('connect', () => {
        this.isConnected = true
        this.connectRetryCount = 0
      })
      socket.on('disconnect', async (reason) => {
        this.isConnected = false
        this.connectRetryCount = 0
        if(reason !== 'io server disconnect' || !authStore.hasAuthToken) {
          return
        }
        try {
          await authStore.renewToken()
        } catch (e) {
          return
        }
        if(!authStore.hasAuthToken) {
          return
        }
        this.connect()
      })
      socket.on(GlobalSocketEventType.TokenAboutToExpire, async () => {
        // eslint-disable-next-line no-console
        console.log('TokenAboutToExpire')
        await authStore.renewToken()
      })
      socket.on(GlobalSocketEventType.TokenExpired, async () => {
        // eslint-disable-next-line no-console
        console.log('TokenExpired')
        await authStore.renewToken()
        this.connect()
      })
      socket.on('connect_error', async (err) => {
        this.isConnected = false

        if(err.message === KnownError.FORBIDDEN) {
          // eslint-disable-next-line no-console
          console.log('connect_error - forbidden')
        }
        if(err.message !== KnownError.FORBIDDEN) {
          return
        }
        if(this.connectRetryCount >= MAX_FORBIDDEN_CONNECT_RETRY_COUNT) {
          authStore.logout()
          return
        }
        if(!authStore.hasAuthToken) {
          return
        }
        this.connectRetryCount++
        await authStore.renewToken()
        const retryDelayMs = Math.min(
          FORBIDDEN_CONNECT_RETRY_DELAY_STEP_MS * (this.connectRetryCount - 1),
          MAX_FORBIDDEN_CONNECT_RETRY_DELAY_MS,
        )
        if(retryDelayMs) {
          await new Promise(resolve => setTimeout(resolve, retryDelayMs))
        }
        if(!authStore.hasAuthToken) {
          return
        }
        this.connect()
      })

      for(const method of this.methodsWaitingToBeInjected) {
        if(method.action === 'onAny' || method.action === 'offAny') {
          socket[method.action](method.fn)
        } else {
          socket[method.action](method.eventType, method.fn)
        }
      }
      this.methodsWaitingToBeInjected = []
      reconnectWatchdogInterval = setInterval(() => {
        if(this.isConnected || !authStore.hasAuthToken) {
          return
        }
        this.connect()
      }, 30000)
      visibilityChangeHandler = () => {
        if(document.visibilityState !== 'visible' || this.isConnected || !authStore.hasAuthToken) {
          return
        }
        this.connect()
      }
      document.addEventListener('visibilitychange', visibilityChangeHandler)
      watch(() => hasAuthToken, () => {
        if(authStore.hasAuthToken) {
          this.connect()
        } else {
          this.disconnect()
        }
      }, {
        immediate: true,
      })
    },
    connect() {
      if(this.isConnected) {
        return
      }
      const socket = this.socket
      socket.connect()
    },
    disconnect() {
      if(!this.isConnected) {
        return
      }
      const socket = this.socket
      socket.disconnect()
    },
    dispose() {
      if(reconnectWatchdogInterval) {
        clearInterval(reconnectWatchdogInterval)
        reconnectWatchdogInterval = null
      }
      if(visibilityChangeHandler) {
        document.removeEventListener('visibilitychange', visibilityChangeHandler)
        visibilityChangeHandler = null
      }
      this.disconnect()
    },
  },
})
